import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import UserAPI from '@/api/user.api';
import { isConflict, unwrapApiResult } from './api-result';
import type { ChangePrimaryEmailResult, ManagedUser } from './types';

const userEmailsKeys = {
  all: ['user-emails'] as const,
  byEmail: (email: string) => [...userEmailsKeys.all, 'by-email', email] as const,
};

const findUsers = async ({ email }: { email: string }) => {
  const { users } = unwrapApiResult<{ users?: ManagedUser[] }>(
    await UserAPI.findUsersByEmail({ email }),
  );
  return users ?? [];
};

/**
 * Accounts whose primary or secondary email matches, including deleted ones. Never served from
 * cache: this is the source of truth for a destructive tool.
 *
 * @param email - Address to look up; the query stays idle while it is empty.
 */
export const useUsersByEmail = (email: string) =>
  useQuery({
    queryKey: userEmailsKeys.byEmail(email),
    queryFn: () => findUsers({ email }),
    enabled: Boolean(email),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

/**
 * Finds the other account that holds an address, e.g. after an add was rejected with a conflict.
 *
 * @returns A function resolving to the holder, or null when none is found or the lookup fails.
 */
export const useFindEmailHolder = () => {
  const queryClient = useQueryClient();
  return async ({ email, excludeUserId }: { email: string; excludeUserId: string }) => {
    try {
      const users = await queryClient.fetchQuery({
        queryKey: userEmailsKeys.byEmail(email),
        queryFn: () => findUsers({ email }),
        staleTime: 0,
        gcTime: 0,
      });
      return (
        users.find(
          ({ id, email: primary, secondaryEmails }) =>
            id !== excludeUserId &&
            (primary === email || secondaryEmails.some(secondary => secondary.email === email)),
        ) ?? null
      );
    } catch {
      return null;
    }
  };
};

const useInvalidateLookup = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: userEmailsKeys.all });
};

/**
 * Adds a secondary email to a user. The lookup is refetched once the request settles.
 */
export const useAddSecondaryEmail = () => {
  const invalidateLookup = useInvalidateLookup();
  return useMutation({
    mutationFn: async (args: { userId: string; email: string; reason: string }) =>
      unwrapApiResult(await UserAPI.addSecondaryEmail(args)),
    // A 409 wrote nothing; refetching would unmount the card before it shows the conflict.
    onSettled: (_result, error) => (isConflict(error) ? undefined : invalidateLookup()),
  });
};

/**
 * Removes a secondary email from a user, which also releases it from a deleted account. The
 * lookup is refetched once the request settles, since a failed response may follow a committed write.
 */
export const useRemoveSecondaryEmail = () => {
  const invalidateLookup = useInvalidateLookup();
  return useMutation({
    mutationFn: async (args: { userId: string; email: string; reason: string }) =>
      unwrapApiResult(await UserAPI.removeSecondaryEmail(args)),
    onSettled: () => invalidateLookup(),
  });
};

/**
 * Moves an address off a deleted account and adds it to another user as a secondary email. A
 * failure to release rejects; a failure to add after a successful release resolves with `addError`
 * so the caller can report the half-done state. The lookup is refetched either way.
 */
export const useReleaseAndAddEmail = () => {
  const invalidateLookup = useInvalidateLookup();
  return useMutation({
    mutationFn: async ({
      holderId,
      userId,
      email,
      reason,
    }: {
      holderId: string;
      userId: string;
      email: string;
      reason: string;
    }) => {
      unwrapApiResult(await UserAPI.removeSecondaryEmail({ userId: holderId, email, reason }));
      try {
        unwrapApiResult(await UserAPI.addSecondaryEmail({ userId, email, reason }));
        return { addError: null };
      } catch (addError) {
        return { addError };
      }
    },
    onSettled: () => invalidateLookup(),
  });
};

/**
 * Previews (`dryRun`) or applies a primary email change. Applying refetches the lookup. Always pass
 * `oldEmail`, the primary as shown when the panel opened: it makes the transport's automatic retry
 * of a failed request safe and turns a stale panel into a conflict instead of a silent no-op.
 */
export const useChangePrimaryEmail = () => {
  const invalidateLookup = useInvalidateLookup();
  return useMutation({
    mutationFn: async (args: {
      userId: string;
      email: string;
      oldEmail: string;
      keepOldAsSecondary: boolean;
      reason?: string;
      dryRun: boolean;
    }) => unwrapApiResult<ChangePrimaryEmailResult>(await UserAPI.changePrimaryEmail(args)),
    onSettled: (_result, _error, { dryRun }) => (dryRun ? undefined : invalidateLookup()),
  });
};
